import type { ObjectId, SpatialObject, WorkspaceDocument } from '@/types';
import { isContainer } from '@/types';
import { isObjectKind, objectType } from './objectTypes';
import { isConnectionType } from '@/types';
import { ACYCLIC_TYPES, findCycles } from '@/core/graph/connectionGraph';

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  readonly severity: ValidationSeverity;
  readonly code:
    | 'invalid-id'
    | 'id-mismatch'
    | 'unknown-kind'
    | 'invalid-dimensions'
    | 'invalid-transform'
    | 'malformed-object'
    | 'invalid-parent'
    | 'parent-not-container'
    | 'circular-hierarchy'
    | 'child-list-mismatch'
    | 'malformed-connection'
    | 'circular-connection'
    | 'kind-specific';
  readonly objectId?: ObjectId;
  readonly connectionId?: string;
  readonly message: string;
}

export interface ValidationResult {
  readonly ok: boolean;
  readonly issues: readonly ValidationIssue[];
}

const isFinitePositive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export const isValidId = (id: unknown): id is string => typeof id === 'string' && ID_PATTERN.test(id);

/**
 * Structural validation of a workspace document. Errors mean the document must
 * not be loaded as-is; warnings are inconsistencies the scene graph tolerates
 * (and `repairDocument` can fix) but that indicate a bug upstream.
 */
export const validateDocument = (doc: WorkspaceDocument): ValidationResult => {
  const issues: ValidationIssue[] = [];
  const objects = doc.objects;
  const keys = Object.keys(objects) as ObjectId[];

  for (const key of keys) {
    const obj = objects[key];
    if (!obj) continue;
    const rawId: unknown = obj.id;
    if (!isValidId(rawId)) {
      issues.push({
        severity: 'error',
        code: 'invalid-id',
        objectId: key,
        message: 'object has an invalid id ' + JSON.stringify(rawId),
      });
    } else if (obj.id !== key) {
      issues.push({
        severity: 'error',
        code: 'id-mismatch',
        objectId: key,
        message: `object "${obj.id}" is stored under key "${key}"`,
      });
    }
    const kind: unknown = obj.kind;
    if (!isObjectKind(kind)) {
      issues.push({
        severity: 'error',
        code: 'unknown-kind',
        objectId: key,
        message: 'unknown object kind ' + JSON.stringify(kind),
      });
      continue;
    }
    // Untrusted input may be missing whole sub-objects; read them defensively so a
    // corrupt snapshot produces an issue instead of a TypeError during boot.
    const loose = obj as unknown as {
      size?: Partial<Record<'width' | 'height', unknown>> | null;
      transform?: {
        position?: Partial<Record<'x' | 'y', unknown>> | null;
        rotation?: unknown;
        scale?: Partial<Record<'x' | 'y', unknown>> | null;
      } | null;
      metadata?: { tags?: unknown } | null;
      childIds?: unknown;
    };
    if (!isFinitePositive(loose.size?.width) || !isFinitePositive(loose.size?.height)) {
      issues.push({
        severity: 'error',
        code: 'invalid-dimensions',
        objectId: key,
        message: 'size must be finite and positive',
      });
    }
    const t = loose.transform;
    if (
      !isFiniteNumber(t?.position?.x) ||
      !isFiniteNumber(t?.position?.y) ||
      !isFiniteNumber(t?.rotation) ||
      !isFiniteNumber(t?.scale?.x) ||
      !isFiniteNumber(t?.scale?.y) ||
      !isFinitePositive(Math.abs(t.scale.x)) ||
      !isFinitePositive(Math.abs(t.scale.y))
    ) {
      issues.push({
        severity: 'error',
        code: 'invalid-transform',
        objectId: key,
        message: 'transform contains non-finite or zero values',
      });
    }
    if (!Array.isArray(loose.metadata?.tags) || !isFiniteNumber(obj.zIndex)) {
      issues.push({
        severity: 'error',
        code: 'malformed-object',
        objectId: key,
        message: 'metadata.tags must be an array and zIndex a finite number',
      });
    }
    if (isContainer(obj) && !Array.isArray(loose.childIds)) {
      issues.push({
        severity: 'error',
        code: 'malformed-object',
        objectId: key,
        message: 'container childIds must be an array',
      });
    }
    if (!isFiniteNumber(obj.zIndex)) {
      issues.push({
        severity: 'error',
        code: 'invalid-transform',
        objectId: key,
        message: 'zIndex must be a finite number',
      });
    }
    if (obj.parentId !== null) {
      const parent = objects[obj.parentId];
      if (!parent) {
        issues.push({
          severity: 'error',
          code: 'invalid-parent',
          objectId: key,
          message: `parent "${obj.parentId}" does not exist`,
        });
      } else if (!isContainer(parent)) {
        issues.push({
          severity: 'error',
          code: 'parent-not-container',
          objectId: key,
          message: `parent "${parent.id}" (${parent.kind}) cannot hold children`,
        });
      }
    }
    for (const message of objectType(obj.kind).validate(obj)) {
      issues.push({ severity: 'error', code: 'kind-specific', objectId: key, message });
    }
  }

  issues.push(...detectCycles(objects));
  issues.push(...checkChildLists(objects));

  for (const [key, c] of Object.entries(doc.connections)) {
    if (!isValidId(c.id) || c.id !== key) {
      issues.push({
        severity: 'error',
        code: 'malformed-connection',
        connectionId: key,
        message: 'connection id is invalid or mismatched',
      });
      continue;
    }
    if (!objects[c.sourceId] || !objects[c.targetId]) {
      issues.push({
        severity: 'error',
        code: 'malformed-connection',
        connectionId: key,
        message: `connection "${c.id}" references a missing object`,
      });
    } else if (!isConnectionType(c.type)) {
      issues.push({
        severity: 'error',
        code: 'malformed-connection',
        connectionId: key,
        message: `connection "${c.id}" has an unknown type`,
      });
    } else if (c.sourceId === c.targetId) {
      issues.push({
        severity: 'error',
        code: 'malformed-connection',
        connectionId: key,
        message: `connection "${c.id}" links an object to itself`,
      });
    }
  }

  for (const type of ACYCLIC_TYPES) {
    for (const cycle of findCycles(doc.connections, type)) {
      const [first] = cycle;
      issues.push({
        severity: 'error',
        code: 'circular-connection',
        ...(first ? { objectId: first } : {}),
        message: `"${type}" connections form a cycle through ${cycle.join(' → ')}`,
      });
    }
  }

  return { ok: issues.every((i) => i.severity !== 'error'), issues };
};

/** Finds parentId chains that never reach a root. */
const detectCycles = (objects: WorkspaceDocument['objects']): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const state = new Map<ObjectId, 'visiting' | 'done'>();
  for (const start of Object.keys(objects) as ObjectId[]) {
    const path: ObjectId[] = [];
    let current: ObjectId | null = start;
    while (current !== null) {
      const status = state.get(current);
      if (status === 'done') break;
      if (status === 'visiting') {
        issues.push({
          severity: 'error',
          code: 'circular-hierarchy',
          objectId: current,
          message: `object "${current}" is its own ancestor`,
        });
        break;
      }
      state.set(current, 'visiting');
      path.push(current);
      const parent: ObjectId | null = objects[current]?.parentId ?? null;
      current = parent !== null && objects[parent] ? parent : null;
    }
    for (const id of path) state.set(id, 'done');
  }
  return issues;
};

/** Containers' `childIds` must agree with children's `parentId`. */
const checkChildLists = (objects: WorkspaceDocument['objects']): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const declared = new Map<ObjectId, Set<ObjectId>>();
  for (const obj of Object.values(objects)) {
    if (obj.parentId !== null) {
      let set = declared.get(obj.parentId);
      if (!set) declared.set(obj.parentId, (set = new Set()));
      set.add(obj.id);
    }
  }
  for (const obj of Object.values(objects)) {
    if (!isContainer(obj)) continue;
    const actual = declared.get(obj.id) ?? new Set<ObjectId>();
    const listed = new Set(obj.childIds);
    const same = actual.size === listed.size && [...actual].every((id) => listed.has(id));
    if (!same) {
      issues.push({
        severity: 'warning',
        code: 'child-list-mismatch',
        objectId: obj.id,
        message: `container "${obj.id}" childIds do not match its children's parentId`,
      });
    }
  }
  return issues;
};

/**
 * Best-effort repair of warning-level inconsistencies: rebuilds container
 * `childIds` from the children's `parentId`. Errors are not repaired.
 */
export const repairDocument = (doc: WorkspaceDocument): WorkspaceDocument => {
  const byParent = new Map<ObjectId, ObjectId[]>();
  for (const obj of Object.values(doc.objects)) {
    if (obj.parentId !== null && doc.objects[obj.parentId]) {
      byParent.set(obj.parentId, [...(byParent.get(obj.parentId) ?? []), obj.id]);
    }
  }
  const objects: Record<ObjectId, SpatialObject> = { ...doc.objects };
  for (const obj of Object.values(doc.objects)) {
    if (!isContainer(obj)) continue;
    const childIds = byParent.get(obj.id) ?? [];
    const same = childIds.length === obj.childIds.length && childIds.every((id, i) => obj.childIds[i] === id);
    if (!same) objects[obj.id] = { ...obj, childIds };
  }
  return { ...doc, objects };
};
